import { ReactNode } from 'react';

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  BTC = 'BTC',
  GBP = 'GBP',
}

export enum InvoiceStatus {
  NEW = 'NEW',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  COMPLETED = 'COMPLETED',
}

export type TokenDecoded = {
  email: string;
  username: string;
  exp: number;
  auth_time: number;
  'cognito:groups': `tenant:${TENANT_TYPE}`[];
};

export enum TENANT_TYPE {
  TERAHASH = 'terahash',
  DUCKENERGY = 'duckenergy',
  BIT3_MINING = 'bit3mining',
  CRYPTOHALL = 'cryptohall24',
}

export enum NOTIFICATION_TYPE {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  SUCCESS = 'SUCCESS',
}

export enum STATUS_CODES {
  FORBIDDEN = 403,
  UNAUTHORIZED = 401,
  SERVER_ERROR = 500,
  NOT_FOUND = 404,
  DEPENDENCY_ERROR = 424,
  SUCCESS = 200,
  BAD_REQUEST = 400,
}

export interface Miner {
  id: string;
  userId: string;
  minerTypeId: string;
  macAddress: string;
  ipAddress: string;
  workerName: string;
  miningPool: string;
  stratumAddress: string;
  password: string | null;
  connectedPool: string;
  yesterdaykWhConsumption: number;
  yesterdayUptime: number;
  yesterdayTotalElectricityCost: number;
  yesterdayTHPerMiner: number;
  yesterdayUptimePercentage: number;
  totalEnergyConsumed: number;
  totalErrors: number;
  minerTypeName: string;
  locationId: string;
  locationName: string;
  connectedSince: string;
  totalUptime: number;
  actualHashrate: number;
  status: MinerStatus;
  name: string;
}

export enum MinerStatus {
  MINING = 'MINING',
  OFFLINE = 'OFFLINE',
}

export enum ModalType {
  CONFIRM = 'confirm',
  SETTINGS = 'settings',
  INFO = 'info',
  REPORT = 'report',
}

export interface StoreMiner {
  name: string;
  price: number;
  image: string;
}

export interface IInfoBgCard {
  bgImg: string;
  title: string;
  textColor: string;
  content: ReactNode;
}

export interface PaymentOptions {
  label: string;
  value: string;
  icon: string;
  outlined?: boolean;
}

export type LabelValue<Label extends string | ReactNode, Value> = {
  label: Label;
  value: Value;
};

export enum ThemeKey {
  DUCKENERGY = 'duckenergy',
  AMITYAGE = 'amityage',
  CRYPTOHALL24_DUERR = 'cryptohall24-duerr',
  CRYPTOHALL24_QUETTA = 'cryptohall24-quetta',
  QURUBIQA = 'qurubiqa',
  BITCLUSTER = 'bitcluster',
  PAYMINE = 'paymine',
  DAHAB = 'dahab',
  BMW = 'bmw',
  STELLAR_FORGE = 'stellar-forge',
  PANTHEON = 'pantheon',
  PANTHEON_AUS = 'pantheon-aus',
  AVATRON = 'avatron',
  POLARCORE = 'polarcore',
  ENDLESSMINING = 'endlessmining',
  SABRE56 = 'sabre56',
  CHRONOS_ENERGY = 'chronos-energy',
  HIGGS = 'higgs',
  MAWSON = 'mawson',
  REVOLVE_LABS = 'revolve-labs',
  HEARST = 'hearst',
  DATACUBES = 'datacubes',
  BITASICS = 'bitasics',
  EMCD = 'emcd',
  BLUEFORGE = 'blueforge',
  CRYPTOTECH = 'cryptotech',
  MININGSTORE = 'miningstore',
  HASHLABS = 'hashlabs',
  CORPA = 'corpa',
  TERAHASH = 'terahash',
  FORTRIS = 'fortris',
  YMIR = 'ymir',
  CHAZON = 'chazon',
  MININGWITHBRAD = 'miningwithbrad',
}

export type SubdomainClientInfo = {
  theme: ThemeKey;
  orgId: string;
};

export const subdomainMap: Record<string, SubdomainClientInfo> = {
  my: {
    theme: ThemeKey.DUCKENERGY,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_DUCKENERGY ?? '',
  },
  amityage: {
    theme: ThemeKey.AMITYAGE,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_AMITYAGE ?? '',
  },
  'cryptohall24-duerr': {
    theme: ThemeKey.CRYPTOHALL24_DUERR,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_CRYPTOHALL_DUERR ?? '',
  },
  'cryptohall24-quetta': {
    theme: ThemeKey.CRYPTOHALL24_QUETTA,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_CRYPTOHALL_QUETTA ?? '',
  },
  qurubiqa: {
    theme: ThemeKey.QURUBIQA,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_QURUBIQA ?? '',
  },
  bitcluster: {
    theme: ThemeKey.BITCLUSTER,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_BITCLUSTER ?? '',
  },
  paymine: {
    theme: ThemeKey.PAYMINE,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_PAYMINE ?? '',
  },
  dahab: {
    theme: ThemeKey.DAHAB,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_DAHAB ?? '',
  },
  bmw: {
    theme: ThemeKey.BMW,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_BMW ?? '',
  },
  'stellar-forge': {
    theme: ThemeKey.STELLAR_FORGE,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_STELLAR_FORGE ?? '',
  },
  pantheon: {
    theme: ThemeKey.PANTHEON,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_PANTHEON ?? '',
  },
  'pantheon-aus': {
    theme: ThemeKey.PANTHEON_AUS,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_PANTHEON_AUS ?? '',
  },
  avatron: {
    theme: ThemeKey.AVATRON,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_AVATRON ?? '',
  },
  polarcore: {
    theme: ThemeKey.POLARCORE,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_POLARCORE ?? '',
  },
  endlessmining: {
    theme: ThemeKey.ENDLESSMINING,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_ENDLESSMINING ?? '',
  },
  sabre56: {
    theme: ThemeKey.SABRE56,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_SABRE56 ?? '',
  },
  'chronos-energy': {
    theme: ThemeKey.CHRONOS_ENERGY,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_CHRONOS_ENERGY ?? '',
  },
  higgs: {
    theme: ThemeKey.HIGGS,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_HIGGS ?? '',
  },
  mawson: {
    theme: ThemeKey.MAWSON,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_MAWSON ?? '',
  },
  'revolve-labs': {
    theme: ThemeKey.REVOLVE_LABS,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_REVOLVE_LABS ?? '',
  },
  hearst: {
    theme: ThemeKey.HEARST,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_HEARST ?? '',
  },
  datacubes: {
    theme: ThemeKey.DATACUBES,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_DATACUBES ?? '',
  },
  bitasics: {
    theme: ThemeKey.BITASICS,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_BITASICS ?? '',
  },
  emcd: {
    theme: ThemeKey.EMCD,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_EMCD ?? '',
  },
  blueforge: {
    theme: ThemeKey.BLUEFORGE,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_BLUEFORGE ?? '',
  },
  cryptotech: {
    theme: ThemeKey.CRYPTOTECH,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_CRYPTOTECH ?? '',
  },
  miningstore: {
    theme: ThemeKey.MININGSTORE,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_MININGSTORE ?? '',
  },
  hashlabs: {
    theme: ThemeKey.HASHLABS,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_HASHLABS ?? '',
  },
  corpa: {
    theme: ThemeKey.CORPA,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_CORPA ?? '',
  },
  terahash: {
    theme: ThemeKey.TERAHASH,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_TERAHASH ?? '',
  },
  fortris: {
    theme: ThemeKey.FORTRIS,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_FORTRIS ?? '',
  },
  ymir: {
    theme: ThemeKey.YMIR,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_YMIR ?? '',
  },
  chazon: {
    theme: ThemeKey.CHAZON,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_CHAZON ?? '',
  },
  miningwithbrad: {
    theme: ThemeKey.MININGWITHBRAD,
    orgId: process.env.REACT_APP_AUTH0_ORG_ID_MININGWITHBRAD ?? '',
  },
};
